import React from 'react';
import {
  Card,
  CardHeader,
  Container,
  Row,
  Col,
  Input,
  Button,
  Table,
  Spinner,
  Badge,
  Alert,
} from 'reactstrap';
import Header from '../../components/Headers/Header.js';
import { axiosGet, axiosPut, axiosDelete } from '../../util/apiConfig';

const NUMERIC_FIELDS = ['price50', 'price25', 'priceTrial', 'noShowValue', 'conversionIncentive'];

const emptyForm = () => ({
  price50: '', price25: '', priceTrial: '', noShowMode: 'flat', noShowValue: '', conversionIncentive: '',
});

const formFromSettings = (s) => (s ? {
  price50: String(s.price50 ?? ''),
  price25: String(s.price25 ?? ''),
  priceTrial: String(s.priceTrial ?? ''),
  noShowMode: s.noShowMode || 'flat',
  noShowValue: String(s.noShowValue ?? ''),
  conversionIncentive: String(s.conversionIncentive ?? ''),
} : emptyForm());

class TeacherPayoutSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: [],
      loading: true,
      editingId: null,
      form: emptyForm(),
      saving: false,
      successMsg: '',
      errorMsg: '',
    };
  }

  componentDidMount() {
    this.loadRows();
  }

  loadRows = () => {
    this.setState({ loading: true, errorMsg: '' });
    axiosGet('payout/teacher-settings')
      .then((res) => {
        this.setState({ rows: Array.isArray(res.data) ? res.data : [], loading: false });
      })
      .catch((err) => {
        this.setState({ loading: false, rows: [], errorMsg: (err && err.message) || 'Could not load teacher settings' });
      });
  };

  startEdit = (row) => {
    this.setState({ editingId: row.teacherId, form: formFromSettings(row.settings), successMsg: '', errorMsg: '' });
  };

  cancelEdit = () => {
    this.setState({ editingId: null, form: emptyForm() });
  };

  handleChange = (field) => (e) => {
    const { value } = e.target;
    this.setState((prev) => ({ form: { ...prev.form, [field]: value } }));
  };

  save = (teacherId) => {
    this.setState({ saving: true, successMsg: '', errorMsg: '' });
    const payload = { noShowMode: this.state.form.noShowMode };
    NUMERIC_FIELDS.forEach((f) => {
      payload[f] = this.state.form[f] === '' ? 0 : Number(this.state.form[f]);
    });
    axiosPut(`payout/teacher-settings/${teacherId}`, payload)
      .then(() => {
        this.setState({ saving: false, editingId: null, form: emptyForm(), successMsg: 'Teacher payout settings saved' });
        this.loadRows();
      })
      .catch((err) => {
        this.setState({ saving: false, errorMsg: (err && err.message) || 'Could not save settings' });
      });
  };

  resetToDefault = (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove custom payout settings for ${row.teacherName || row.teacherId}? They will fall back to the global default rates.`)) return;
    axiosDelete(`payout/teacher-settings/${row.teacherId}`)
      .then(() => {
        this.setState({ successMsg: 'Reverted to default rates' });
        this.loadRows();
      })
      .catch((err) => this.setState({ errorMsg: (err && err.message) || 'Could not reset settings' }));
  };

  render() {
    const { rows, loading, editingId, form, saving, successMsg, errorMsg } = this.state;

    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Card className="shadow">
            <CardHeader className="border-0">
              <h3 className="mb-0">Teacher Payout Settings</h3>
              <small className="text-muted">
                Override payout rates per teacher. A teacher with no custom settings uses the global default rates
                configured on the Payout Settings page.
              </small>
              {successMsg ? <Alert color="success" className="mt-3 mb-0 py-2">{successMsg}</Alert> : null}
              {errorMsg ? <Alert color="danger" className="mt-3 mb-0 py-2">{errorMsg}</Alert> : null}
            </CardHeader>

            <div style={{ overflowX: 'auto' }}>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Teacher</th>
                    <th scope="col">Status</th>
                    <th scope="col">50-min</th>
                    <th scope="col">25-min</th>
                    <th scope="col">Trial</th>
                    <th scope="col">No-show</th>
                    <th scope="col">Conversion</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="8" className="text-center"><Spinner /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan="8" className="text-center text-muted">No teachers found.</td></tr>
                  ) : (
                    rows.map((row) => {
                      const editing = editingId === row.teacherId;
                      return (
                        <tr key={row.teacherId}>
                          <td>
                            <span className="font-weight-bold">{row.teacherName || row.teacherId}</span>
                            <br />
                            <small className="text-muted">{row.teacherEmail}</small>
                          </td>
                          <td>
                            {row.hasCustomSettings ? (
                              <Badge color="success">Custom rates</Badge>
                            ) : (
                              <Badge color="warning" title="Falls back to the global default rates">
                                No settings defined — using default
                              </Badge>
                            )}
                          </td>
                          {editing ? (
                            <>
                              <td><Input type="number" bsSize="sm" value={form.price50} onChange={this.handleChange('price50')} /></td>
                              <td><Input type="number" bsSize="sm" value={form.price25} onChange={this.handleChange('price25')} /></td>
                              <td><Input type="number" bsSize="sm" value={form.priceTrial} onChange={this.handleChange('priceTrial')} /></td>
                              <td>
                                <Input type="select" bsSize="sm" className="mb-1" value={form.noShowMode} onChange={this.handleChange('noShowMode')}>
                                  <option value="flat">Flat</option>
                                  <option value="percent">Percent</option>
                                </Input>
                                <Input type="number" bsSize="sm" value={form.noShowValue} onChange={this.handleChange('noShowValue')} />
                              </td>
                              <td><Input type="number" bsSize="sm" value={form.conversionIncentive} onChange={this.handleChange('conversionIncentive')} /></td>
                              <td>
                                <Button size="sm" color="primary" onClick={() => this.save(row.teacherId)} disabled={saving}>
                                  {saving ? 'Saving…' : 'Save'}
                                </Button>
                                <Button size="sm" color="secondary" outline onClick={this.cancelEdit} disabled={saving}>
                                  Cancel
                                </Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{row.settings ? row.settings.price50 : '—'}</td>
                              <td>{row.settings ? row.settings.price25 : '—'}</td>
                              <td>{row.settings ? row.settings.priceTrial : '—'}</td>
                              <td>{row.settings ? `${row.settings.noShowValue} (${row.settings.noShowMode})` : '—'}</td>
                              <td>{row.settings ? row.settings.conversionIncentive : '—'}</td>
                              <td>
                                <Button size="sm" color="primary" outline onClick={() => this.startEdit(row)}>
                                  {row.hasCustomSettings ? 'Edit' : 'Set custom rates'}
                                </Button>
                                {row.hasCustomSettings ? (
                                  <Button size="sm" color="danger" outline onClick={() => this.resetToDefault(row)}>
                                    Reset
                                  </Button>
                                ) : null}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </Container>
      </>
    );
  }
}

export default TeacherPayoutSettings;
